import { api } from "./client";

// Резервации на маси. BE: app/features/reservations; договорът —
// docs/reservations.md в BE репото. Всички пътища искат правото
// `view_reservations` — освен двата на „Настани" от Маркиране в края
// (`seat_reservations`). Времената идват в UTC с „Z". Резервациите НЕ закриват
// и не пипат сметки — плащането и закриването са само в Маркиране.

// "expired" — „Неизпълнена": предстояща, чийто край (начало +
// продължителност) мина, без да е настанена или отказана. BE я прехвърля при
// четене; не заема масата, не се настанява/редактира/връща. Пази се в базата
// за справките (docs/cross-repo-contract.md).
export type ReservationStatus =
  | "upcoming"
  | "seated"
  | "completed"
  | "cancelled"
  | "expired";

export interface Reservation {
  id: number;
  startAt: string;
  durationMinutes: number;
  guests: number;
  guestName: string;
  phone: string;
  note: string | null;
  // Остава в договора (default false), но UI не го показва и не го праща.
  smsConfirmation: boolean;
  status: ReservationStatus;
  // По реда на избора; първата маса дава името на сметката при настаняване.
  tableIds: number[];
  tableNames: string[];
  // Главната сметка — на първата маса.
  billId: number | null;
  // ВСИЧКИ сметки на резервацията, главната първа: при „Отделна сметка за
  // всяка маса" и сметките на другите маси. Касата в локален режим (и
  // по-стар BE) не го праща — тогава важи само `billId`
  // (`reservationBillIds`).
  billIds?: number[];
  seatedAt: string | null;
  releasedAt: string | null;
  createdAt: string;
}

/** Отворена сметка (на който и да е сервитьор) — масата с това име е заета. */
export interface OpenTable {
  billId: number;
  tableName: string;
  // Кога е отворена (или възстановена) сметката.
  openedAt: string;
  // Сервитьорът на сметката — за избора „При кой сервитьор да настаним?";
  // името е null, ако потребителят не е намерен.
  waiterId: number | null;
  waiterName: string | null;
  // Сумата на сметката (Decimal като низ, както bills.total_amount) — в
  // избора „При кой сервитьор" се вижда при кого реално седят гостите.
  totalAmount: string;
  // Резервацията, чиято сметка е (в какъвто и да е статус — настанена,
  // „Гостите си тръгнаха", възстановена); null — сметка без резервация.
  // Сметка на ДРУГА резервация никога не се закача към нова резервация
  // (BE seat → 409) — FE не я предлага (`seatCheckOf`). ПРАЗНАТА сметка на
  // ненастанена резервация (тръгнали си без поръчка) идва с null — масата е
  // свободна и следващото „Настани" я поема.
  reservationId: number | null;
  // „Отмени настаняването" би премахнало тази сметка СЕГА: празна (никой не
  // е поръчал), отворена от самото „Настани" и още на натисналия. Липсва при
  // по-стар BE → третира се като false (сметката остава).
  closesOnUnseat?: boolean;
  // Само в `GET /reservations/seatable`: сметка, която гледащият НЕ вижда в
  // Маркиране (чужда, без `view_all_users_bills`) — сумата е скрита („0.00"),
  // масата и сервитьорът остават („Маса 2 е заета от Мария").
  hidden?: boolean;
}

export interface ReservationsDay {
  // YYYY-MM-DD — локалният ден (Europe/Sofia) на startAt.
  date: string;
  reservations: Reservation[];
  openTables: OpenTable[];
  // Настанените СЕГА резервации, които не са в `reservations` (за ден ≠
  // днес — всички настанени): само за проверката на „Настани" / „При кой
  // сервитьор" (масата им е заета); не се показват в списъка/линията на деня.
  seatedNow: Reservation[];
}

export interface ReservationCreateIn {
  // ISO; без зона BE го тълкува като локално време.
  startAt: string;
  durationMinutes: number;
  guests: number;
  guestName: string;
  phone: string;
  note?: string;
  // SMS потвърждението е скрито: FE не праща полето (BE default false).
  smsConfirmation?: boolean;
  tableIds: number[];
}

// GET /reservations?date=YYYY-MM-DD — резервациите на деня + отворените сметки.
// При всяко четене BE прехвърля настанените със закрита сметка в „Минали", а
// предстоящите с изтекъл край — в „Неизпълнена" (expired). За днес идват и
// вчерашните настанени и предстоящи, чийто час още тече след полунощ.
export const getReservationsDay = async (date: string): Promise<ReservationsDay> => {
  const response = await api.get<ReservationsDay>("/reservations", { params: { date } });
  return response.data;
};

export const createReservation = async (data: ReservationCreateIn): Promise<Reservation> => {
  const response = await api.post<Reservation>("/reservations", data);
  return response.data;
};

// PATCH /reservations/{id} — редакция на ПРЕДСТОЯЩА резервация (409 иначе).
// Тялото е като при създаване (всички полета); застъпването се проверява без
// самата нея.
export const updateReservation = async (
  id: number,
  data: ReservationCreateIn,
): Promise<Reservation> => {
  const response = await api.patch<Reservation>(`/reservations/${id}`, data);
  return response.data;
};

// Действията по статуса (никое не закрива и не пипа сметка):
// - seat — тяло по избор `{ billId }`: закача към тази отворена сметка по
//   масите на резервацията; `{ separateBills: true }` (резервация с няколко
//   маси, само с интернет): по маса — нова сметка, закачане към единствената
//   отворена, а при няколко отворени на една маса — с `billId` (избраната;
//   масите с няколко взимат сметката на нейния сервитьор, новите са на
//   него), иначе 409; без тяло — 0 отворени → нова празна сметка на
//   първата маса, 1 → закача към нея, 2+ → 409 (FE пита предварително при
//   кой сервитьор и праща billId); 409 и при отворена сметка на ДРУГА
//   резервация по масите (на настанена — „Маса N е заета…", иначе „Сметката
//   на предишната резервация на маса N (…) още не е закрита" — никога не се
//   закача, и с billId), при маса на друга настанена и по-рано
//   от SEAT_EARLY_MINUTES (30) мин преди началото; нова сметка от seat е на
//   сервитьора, който поръча първи в нея;
// - release — само приключва резервацията; отворената сметка остава и
//   масата е заета, докато сметката не се закрие в Маркиране;
// - unseat — настанена → предстояща, само откача; сметката остава; 409 след
//   края на часа на резервацията (`canUnseat`);
// - restore — отказана → предстояща (409 при застъпване).
export type ReservationAction = "seat" | "release" | "cancel" | "unseat" | "restore";

/**
 * Отговорът на действие. Само seat носи `billCreated`: true — отворена е нова
 * празна сметка (тя става на сервитьора, който поръча първи), false —
 * закачена към отворена. Само unseat носи `billClosed`: true — празната
 * сметка от „Настани" е премахната, false — сметката остава в Маркиране.
 */
export type ReservationActionResult = Reservation & { billCreated?: boolean; billClosed?: boolean };

export const runReservationAction = async (
  id: number,
  action: ReservationAction,
  // Само за seat: отворената сметка, към която да се закачи.
  billId?: number,
  // Само за seat: „Отделна сметка за всяка маса" (с `billId` — избраната).
  separateBills?: boolean,
): Promise<ReservationActionResult> => {
  const body =
    action !== "seat"
      ? undefined
      : separateBills
        ? { ...(billId !== undefined ? { billId } : {}), separateBills: true }
        : billId !== undefined
          ? { billId }
          : undefined;
  const response = await api.post<ReservationActionResult>(`/reservations/${id}/${action}`, body);
  return response.data;
};

/**
 * Познатият телефон в „Нова резервация" — всички резервации на същия
 * нормализиран телефон (ключът на справка „Гости": `+359 888…` = `0888…`),
 * без период. `reservations` = 0 — непознат номер, останалото е празно.
 * `visits` — настанени + приключили; `averageBill` (Decimal низ) — само по
 * приключилите; `lastNote` — най-новата непразна забележка, `lastNoteAt` —
 * началото на резервацията ѝ.
 */
export interface GuestHistory {
  phoneKey: string;
  // Името от най-новата резервация — с него формата попълва празното поле.
  guestName: string | null;
  reservations: number;
  upcoming: number;
  seated: number;
  completed: number;
  cancelled: number;
  expired: number;
  visits: number;
  averageBill: string | null;
  lastVisitAt: string | null;
  lastNote: string | null;
  lastNoteAt: string | null;
}

// GET /reservations/guest-history?phone=…&excludeId=… — правото на модула
// (`view_reservations`), не на справката. `excludeId` — редактираната
// резервация (не е своя история). Не е в одита.
export const getGuestHistory = async (phone: string, excludeId: number | null): Promise<GuestHistory> => {
  const params = excludeId === null ? { phone } : { phone, excludeId };
  const response = await api.get<GuestHistory>("/reservations/guest-history", { params });
  return response.data;
};

// ── „Настани" от Маркиране (право `seat_reservations`, без модула) ──────────

/** Резервация в модала „Настани" на Маркиране — плюс залите на масите ѝ (по
 *  реда на `tableNames`) и кой я е създал. */
export interface SeatableReservation extends Reservation {
  regionNames: string[];
  createdByName: string | null;
}

/**
 * GET /reservations/seatable — предстоящите за настаняване днес (и след
 * полунощ, щом прозорецът на „Настани" се е отворил), отворените сметки и
 * сега настанените (заетостта). Формата е като `ReservationsDay`, за да
 * работи същата проверка (`seatCheckOf`).
 */
export interface SeatableReservations {
  reservations: SeatableReservation[];
  openTables: OpenTable[];
  seatedNow: Reservation[];
  // Кой гледа и дали номерацията е лична — изборът „къде да настаним" зависи от тях.
  viewerId: number;
  personalNumbering: boolean;
}

export const getSeatableReservations = async (): Promise<SeatableReservations> => {
  const response = await api.get<SeatableReservations>("/reservations/seatable");
  return response.data;
};

/** Изборът на сервитьора: към отворена сметка или нова своя маса. */
export type OrderingSeatTarget = { billId: number } | { newBill: true };

/**
 * POST /reservations/{id}/seat-from-ordering — същото настаняване, но с
 * ИЗРИЧНИЯ избор (FE винаги пита): `{billId}` — своя или видима чужда сметка;
 * `{newBill: true}` — нова своя маса. BE проверява избора спрямо правото
 * `view_all_users_bills` и номерацията (409 „Маса N е заета от …", „Маса N
 * вече е отворена от теб — избери я"). `separateBills` (резервация с няколко
 * маси, само с интернет) — изборът е главната сметка, а всяка друга маса взима
 * своя: нова, или единствената отворена, ако сервитьорът я вижда (иначе 409
 * „Маса N е заета от …"); няколко отворени на маса → 409.
 */
export const seatReservationFromOrdering = async (
  id: number,
  target: OrderingSeatTarget,
  separateBills = false,
): Promise<ReservationActionResult> => {
  const body = separateBills ? { ...target, separateBills: true } : target;
  const response = await api.post<ReservationActionResult>(`/reservations/${id}/seat-from-ordering`, body);
  return response.data;
};

// GET /reservations/edge-view — изгледът за КАСАТА (локалния резерв): суровите
// резервации в прозореца [днес−2, днес+15] + всички настанени, заедно с масите
// и регионите от същата транзакция. FE-то не го показва — тегли го само
// `lib/edge/edgeReservationsFeed.ts`, а response интерсепторът го огледва към
// касата (`PUT {edge}/mirror`). Право: `view_reservations`. Формата е договор
// с BE и касата — FE-то я пренася, без да я чете, затова тук е `unknown`.
export const RESERVATIONS_EDGE_VIEW_PATH = "/reservations/edge-view";

export const getReservationsEdgeView = async (): Promise<unknown> => {
  const response = await api.get<unknown>(RESERVATIONS_EDGE_VIEW_PATH);
  return response.data;
};
